import { Box, Button, Grid, Link, Typography } from "@mui/material";
import fairPayBg from "assets/fairpay/fair-pay-bg.png";
import logo from "assets/logos/logo-transparent.png";
import { AlertContext } from "contexts/Alert";
import { useContext } from "react";

import { FairPaySurveyResponse } from "features/FairPaySurvey/schemas";
import { trackEvent } from "utils/tracking";

const styles = {
  logo: {
    position: "absolute",
    width: "80px",
    top: 20,
    left: 20,
  },
  badge: {
    my: 2,
    backgroundColor: "#B1B3F8",
    backgroundImage: `url(${fairPayBg})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    p: 2,
    borderRadius: "20px",
    minHeight: "560px",
    position: "relative",
  },
  descriptionLargeText: {
    fontSize: 42,
    lineHeight: 1.2,
    fontWeight: 700,
    width: "100%",
    color: "primary.main",
    textAlign: "center",
  },
  descriptionSmallText: {
    fontSize: 20,
    width: "100%",
    fontWeight: 700,
    color: "#151F16",
    textTransform: "none",
    textAlign: "center",
  },
  descriptionTinyText: {
    fontSize: 12,
    mt: 0.5,
    width: "100%",
    textAlign: "center",
  },
  badgeIcon: {
    color: "primary.main",
  },
  fairPayVerifiedBox: {
    position: "absolute",
    bottom: "40px",
    left: "10px",
    right: "10px",
  },
  button: {
    px: 4,
    py: 2,
    textTransform: "none",
    borderRadius: 20,
    my: 2,
    display: "block",
    lineHeight: "1.4",
  },
  copyButton: {
    my: 2,
    textTransform: "none",
    color: "#151F16",
    fontSize: 19,
    fontWeight: 600,
  },
  heading: {
    textAlign: "center",
    maxWidth: "70%",
    fontWeight: 600,
  },
};

export default function FairPayShareBadge({
  surveyResponse,
}: {
  surveyResponse: FairPaySurveyResponse | null;
}) {
  const { setAlert } = useContext(AlertContext);

  if (!surveyResponse?.nichePercentile) {
    return null;
  }
  const topPercentage = 100 - Math.round(surveyResponse.nichePercentile) || 1;

  const getNicheName = () => {
    let nicheResult = surveyResponse.niche;
    if (!nicheResult) {
      return "";
    }

    if (nicheResult === "fashion") {
      return "fashion 👗";
    }
    if (nicheResult === "beauty") {
      return "beauty 💄";
    }
    if (nicheResult === "lifestyle") {
      return "lifestyle 🌿";
    }
    if (nicheResult === "health/wellness") {
      return "wellness 🧘‍♀️";
    }
    if (nicheResult === "pets") {
      return "pets 🐕";
    }
    if (nicheResult === "tech/saas") {
      return "tech/saas 💻";
    }
    if (nicheResult === "coaching/education") {
      return "education 📚";
    }
    if (nicheResult === "home") {
      return "home 🏡";
    }
    if (nicheResult === "kids/family") {
      return "family 👨‍👩‍👧‍👦 ";
    }
    if (nicheResult === "travel") {
      return "travel 🌍";
    }
    if (nicheResult === "food/beverage") {
      return "food 🍳";
    }
    if (nicheResult === "local") {
      return "local 📍";
    }
    if (nicheResult === "other") {
      return "your niche 💰";
    }
  };

  const handleShare = async () => {
    trackEvent("Fair Pay - Share to IG / TikTok Button Clicked", {
      "Survey UUID": surveyResponse.surveyUuid,
    });
    try {
      if (navigator.canShare && navigator.canShare({ text: "hi" })) {
        await navigator.share({
          text: "I just got my Creator Fair Pay Score from Bento! It told me how my creator earnings compare to my peers. Get yours too at onbento.com/fairpay",
        });
      }
    } catch {}
  };

  const handleCopy = async () => {
    trackEvent("Fair Pay - Copy Sharable Link Button Clicked", {
      "Survey UUID": surveyResponse.surveyUuid,
    });
    navigator.clipboard.writeText(
      "I just got my Creator Fair Pay Score from Bento! It told me how my creator earnings compare to my peers. Get yours too at onbento.com/fairpay",
    );
    setAlert("Copied to clipboard!", "success");
  };

  return (
    <>
      <Grid
        item
        xs={12}
        justifyContent="center"
        alignItems="center"
        container
        sx={styles.badge}
      >
        <Box component="img" src={logo} sx={styles.logo} />
        <Grid item xs={12} justifyContent="center" container>
          <Typography sx={styles.descriptionSmallText}>
            {surveyResponse.firstName
              ? `${surveyResponse.firstName}, you're in the`
              : "You're in the"}
          </Typography>
          <Typography sx={styles.descriptionLargeText}>
            TOP {topPercentage}%
          </Typography>
          <Typography sx={[styles.descriptionLargeText, { fontSize: 30 }]}>
            in {getNicheName()}
          </Typography>
          <Typography
            sx={[
              styles.descriptionSmallText,
              { maxWidth: "60%", mt: 2, fontWeight: 400 },
            ]}
          >
            Just got my Fair Pay Score. Get yours too.
          </Typography>
        </Grid>
        <Box sx={styles.fairPayVerifiedBox}>
          <Typography sx={styles.descriptionSmallText}>
            <Box
              component="span"
              className="fa-solid fa-badge-check"
              sx={styles.badgeIcon}
            />{" "}
            Fair Pay Verified
          </Typography>
          <Typography sx={styles.descriptionTinyText} color="textSecondary">
            <em>Brought to you by Bento. The creator pitching tool.</em>
          </Typography>
          <Typography
            sx={[styles.descriptionSmallText, { fontSize: 14, mt: 0.5 }]}
          >
            <Link
              sx={{ color: "#151F16", textTransform: "none" }}
              href="https://onbento.com/fairpay"
              target="_blank"
              rel="noopener noreferrer"
            >
              onbento.com/fairpay
            </Link>
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} justifyContent="center" container>
        <Typography variant="h3" sx={[styles.heading, { mt: 3 }]} paragraph>
          Let's Make Fair Pay the Norm 💰
        </Typography>
      </Grid>

      <Box display="flex" sx={{ width: "100%" }} justifyContent="center">
        <Button
          variant="contained"
          color="primary"
          sx={styles.button}
          onClick={handleShare}
        >
          <span style={{ fontSize: 19 }}>Share to IG / TikTok</span>
          <br />
          <em style={{ fontSize: 12 }}>Help others know their worth</em>
        </Button>
      </Box>

      <Box display="flex" sx={{ width: "100%" }} justifyContent="center">
        <Button sx={styles.copyButton} onClick={handleCopy}>
          🔗 Copy a shareable link
        </Button>
      </Box>
    </>
  );
}
